import {
  CampaignTypeEnum,
  ChannelEnum,
  ModeEnum,
  RFMPeriod,
  StatusEnum,
  TypeEnum,
  WeekDay,
} from "../constants/enums";
import { MessageTemplateData } from "./messageTemplates";
import { Segment } from "./segment";

export type Type = "One-Time" | "Recurring - Weekly" | "Recurring - Monthly";
export type Status =
  | "Draft"
  | "Active"
  | "Scheduled"
  | "Paused"
  | "Completed"
  | "Cancelled";

export type CampaignRegisteredChannel = {
  Id?: number;
  Email: string;
  ContactIdd: string;
  ContactNumber: string;
  ClientId: string;
  Channel: ChannelEnum;
  Status: boolean;
  BrandId: string;
  CreatedDate?: string;
  SettingAccountGroupId?: number | null;
  MinSendLimit?: number;
  MaxSendLimit?: number;
};

export type CampaignRunningCampaign = {
  CampaignDetailsId: number;
  CampaignRunLogId: number;
};

export type CampaignRegisteredChannelLimit = {
  ClientId: string;
  BrandId: string;
  BrandName: string;
  RunningCampaign: CampaignRunningCampaign | null;
  MinSendLimit: number;
  MaxSendLimit: number;
  Status: "Active" | "Inactive";
};

export type CampaignSummaryItem = {
  BrandName: string;
  CampaignId: number;
  CampaignRunLogId: number;
  CampaignName: string;
  TotalTargetMember: number;
  TotalSuccess: number;
  TotalFailed: number;
  TotalPending: number;
  SentToday: number;
  SentTodaySuccess: number;
  SentTodayFailed: number;
};

export const createCampaignRegisteredChannel = (input: {
  email: string;
  clientId: string;
  brandId: string;
  channel: ChannelEnum;
  contactIdd: string;
  contactNumber: string;
}): CampaignRegisteredChannel => ({
  Email: input.email,
  ContactIdd: input.contactIdd,
  ContactNumber: input.contactNumber,

  ClientId: input.clientId,
  Channel: input.channel,
  Status: true,
  BrandId: input.brandId,
});

export type Campaign = {
  Id?: number;
  key: string;
  Name: string;
  Channel: ChannelEnum;
  ScheduleType: TypeEnum;
  Status: StatusEnum;
  CampaignStart?: string;
  CampaignEnd?: string;
  nextSchedule?: string;
  Segments: Segment[];
  criteriaProfile: string;
  CreatedDate: Date;
  CampaignRunLog?: CampaignRunLog[];
  EstimatedTotalMembers?: number;
  Mode?: ModeEnum;
  Budget?: number;
};

export type CampaignDetails = {
  Id?: number;
  Name: string;
  Type?: CampaignTypeEnum;
  RFMPeriod?: RFMPeriod;
  FromAge?: number;
  ToAge?: number;
  Messages: {
    Id?: number;
    Link?: string;
    Content?: string;
    CampaignTemplates?: MessageTemplateData | null;
    CustomCopyText?: string;
    CustomUrl?: string;
    MessageSubject?: string;
    MessagePreheader?: string;
  }[];
  Segments: {
    Id?: number;
    SegmentId: number;
    SegmentName?: string;
  }[];
  PostalDistricts: {
    Id?: number;

    DistrictId: number;
    PostalSectors?: string[];
  }[];
  RFMProfile?: number;
  RFMProfileName?: string;

  BrandId?: string;
  Status?: StatusEnum;
  VoucherId?: number;
  CampaignStart: string;
  CampaignEnd: string;
  Budget?: number;
  EstimatedTotalMembers: number;
  ScheduleType: TypeEnum;
  ImageUrl?: string;
  Channel?: ChannelEnum;
  CreatedDate?: Date;
  SKU?: string;
  SKUList?: string[] | null;
  EVoucherSkus?: { SKU: string }[];
  CampaignRunLog?: CampaignRunLog[];
  PurchaseBrand?: string | null;
  PurchaseBrandList?: string[] | null;
  ExcludeMembers?: ExcludeMember[] | null;
  TemplateType?: number;
  InactiveDays?: number;
  Mode?: ModeEnum;
  IsBilled?: number;
  ReferenceCode?: string;
  PaymentAccountEmail?: string;
  //recurring
  ScheduleEndType?: number;
  MaxOccurrences?: number;
  EndDate?: string;
  RepeatMonth?: number;
  RepeatDays?: string;
  RepeatDaysList?: WeekDay[];
  EstimatedIntervalTime?: number;
  Retargeting?: boolean;
  EnableCustomTrackingLink?: boolean;
  ClearExclusionList?: boolean;
  ExtensionDays?: number;
  TargetMembers?: TargetMember[] | null;
  ClearTargetMembersList?: boolean;
  IncompleteProfileFields?: string[] | null;
};

export type CampaignRunLog = {
  Id: number;
  StartDate?: Date;
  EndDate?: Date;
  Status?: boolean | null;
  IsBilled?: boolean;
  SuccessCount?: number | null;
  FailCount?: number | null;
  TotalMembers: number;
  Clicks?: number | null;
  MemberLogs: any[];
  CreatedDate?: Date;
};

export type CampaignMemberLog = {
  Id: number;
  Name: string;
  ContactIdd: string;
  ContactNumber: string;
  SendDate: Date;
  Status: boolean;
  notes?: string;
  Content?: string | null;
  Image?: string | null;
  Link?: string | null;
  UnsubscribeLink?: string | null;
  Channel?: number | null;
  // Full reply copy for the OK -> Link and STOP -> UnsubscribeLink automated
  // flow — already contains Link/UnsubscribeLink formatted inline, so these
  // are used as-is (through formatMessageContent) rather than templated.
  CopyTextOk?: string | null;
  CopyTextUnsub?: string | null;
};

export type CompletedCampaign = {
  key: string;
  name: string;
  channel: ChannelEnum;
  type: Type;
  completedOn?: string;
  segments: string[];
  criteriaProfile: string;
  ctr: number;
  roi: number;
  revenue: number;
};

export type CompletedCampaignViewModel = {
  CampaignName: string;
  CampaignId: number;
  CampaignRunLogId: number;

  RevenueRewards: number;
  TotalOrderRewards: number;

  RevenueCampaign: number;
  TotalOrderCampaign: number;

  ROI: number;
  TotalFee: number;
  CTR: number;

  Clicks: number;
  MessageDelivered: number;

  Channel: string;
  CampaignStart: string;
  CampaignEnd: string;
  ScheduleType: string;

  RFMProfile: string;
  Segments: { Id: string; Name: string; SegmentId: number }[];
};

export type EstimatedTotalMembersRequest = {
  BrandId: string;
  RFMProfile: number;
  RFMPeriod: number;
  SegmentIds: number[];
  IncludeListMember: boolean;
  FromAge?: number;
  ToAge?: number;
  PostCode?: string[];
  PurchaseBrand?: string[] | null;
  ExcludeMembers?: ExcludeMember[] | null;
  CampaignType?: number;
  TemplateType?: number;
  InactiveDays?: number;
  IncompleteProfileFields?: string[] | null;
};

export type ExcludeMember = {
  Id?: number;
  PhoneNumber?: string;
  CustomerContact?: string;
  Email?: string;
  CreatedDate?: string;
};

export type TargetMember = ExcludeMember & {
  Found?: boolean;
};

export type CampaignInfo = {
  Email: string;
  MemberPortalUrl: string;
  BrandName: string;
  Currency?: string;
};
